import { type StyleConfigProps, type VisibilityElementProps } from '@blaze-runtime/engine-domain';
import { type FontWeightOption, type NumberPrecision } from '@blaze-shared/api-contracts-domain';
import {
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type CanvasElementWithDirtyFieldProps,
  type ChartAggregateFunctionType,
  type ElementHorizontalAlignment,
  type ElementLabelProps,
  type ElementLink,
  type ElementSizingMode,
  type ElementTitleStyleProps,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputColorPaletteModel,
  type PropertiesConfigInputFontStyleOptions,
  type PropertiesConfigInputSpacingAndAlignmentModel,
  type PropertiesConfigInputVisualEffectModel,
  type TableColumnFormat,
} from '@blaze-shared/elements-domain';
import { type SelectOption, type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { type DateXFormatOptions } from './models/helper.types';
import { createSelectOptions } from './utils/chart-select-options.utils';

//#region base

export type DotChartIcons =
  | 'dot-ellipse'
  | 'dot-triangle'
  | 'dot-square'
  | 'dot-diamond'
  | 'dot-plus'
  | 'dot-cross'
  | 'dot-star'
  | 'dot-hexagon';

export const DOT_CHART_ICONS: DotChartIcons[] = [
  'dot-ellipse',
  'dot-triangle',
  'dot-square',
  'dot-diamond',
  'dot-plus',
  'dot-cross',
  'dot-star',
  'dot-hexagon',
];

export const CHART_DIMENSIONS = {
  width: 520,
  height: 400,
  minSize: 200,
  maxWidth: 900,
  maxHeight: 900,
  widthOptions: ['fixed', 'full'] as ElementSizingMode[],
  heightOptions: ['fixed'] as ElementSizingMode[],
};
export const AGGREGATION_FNS: ChartAggregateFunctionType[] = [
  'FIELD VALUE',
  'COUNT',
  'AVG',
  'MAX',
  'MIN',
  'SUM',
];
export const AGGREGATION_FNS_COMPARE: ChartAggregateFunctionType[] = [
  'COUNT',
  'AVG',
  'MAX',
  'MIN',
  'SUM',
];
export const AGGREGATION_OPTIONS: SelectOption<ChartAggregateFunctionType>[] =
  createSelectOptions(AGGREGATION_FNS);
export const AGGREGATION_FNS_COMPARE_OPTIONS: SelectOption<ChartAggregateFunctionType>[] =
  createSelectOptions(AGGREGATION_FNS_COMPARE);
export const NUMBER_FIELD_FORMATS = ['Number', 'Currency', 'Percent'];
export const NUMBER_FIELD_FORMATS_OPTIONS: SelectOption<TableColumnFormat>[] = [
  { label: 'Number', value: 'number' },
  { label: 'Currency', value: 'currency' },
  { label: 'Percent', value: 'percentage' },
];
export type DATE_FORMAT_OPTIONS_VALUES =
  | 'number-month-first'
  | 'number-day-first'
  | 'short-month'
  | 'long-month'
  | 'short-month-year'
  | 'long-month-year'
  | 'number-month-first-year'
  | 'short-month-only'
  | 'long-month-only';
export type TIME_FORMAT_OPTIONS_VALUES = 'with-meridiem' | '24-hour';
export const TIME_FORMATS_OPTIONS: SelectOption<TIME_FORMAT_OPTIONS_VALUES>[] = [
  { label: '03:12 PM', value: 'with-meridiem' },
  { label: '15:12', value: '24-hour' },
];
export const DATE_FORMATS_OPTIONS: SelectOption<DATE_FORMAT_OPTIONS_VALUES>[] = [
  { label: '08/25/2023', value: 'number-month-first' },
  { label: '25/08/2023', value: 'number-day-first' },
  { label: 'Aug 25, 2023', value: 'short-month' },
  { label: 'August 25, 2023', value: 'long-month' },
];
export const MONTH_FORMATS_OPTIONS: SelectOption<DATE_FORMAT_OPTIONS_VALUES>[] = [
  { label: '08/2023', value: 'number-month-first-year' },
  { label: 'Aug 2023', value: 'short-month-year' },
  { label: 'August 2023', value: 'long-month-year' },
];
export const OPTIONS_PICKER_ORDER: SelectOption<string>[] = [
  { value: 'x', label: 'X value' },
  { value: 'y', label: 'Y value' },
];
export const DATE_PERIOD_OPTIONS: SelectOption<string>[] = [
  'Daily',
  'Weekly',
  'Monthly',
  'Quarterly',
  'Yearly',
].map((label) => ({ value: label.toLowerCase(), label }));

type PropsModel = CanvasElementPropertiesWithData & StyleConfigProps & VisibilityElementProps;

export type ChartPropsModelData = {
  multiSeries?: {
    mainValue?: string;
    aggregation?: ChartAggregateFunctionType;
    alias?: string;
    color?: PropertiesConfigInputColorModel;
  }[];
  mainValue?: string;
  aggregation?: ChartAggregateFunctionType;
  alias?: string;

  compareValue?: string;
  compareAggregation?: ChartAggregateFunctionType;

  groupBy?: string;
  segmentData?: string;
  groupByDetails?: string;
  segmentDataDetails?: string;
  order?: string;
  direction?: 'Ascending' | 'Descending';
  maxValue?: string;
  minValue?: string;

  customIcons?: {
    enabled: boolean;
    iconMap: { label: string; icon: string; color: string }[];
  };
};

export type ChartPropsModel = CanvasElementWithDirtyFieldProps &
  PropsModel & {
    content: {
      chart?: {
        /** @deprecated */
        image?: string;
        /** @deprecated */
        imagePosition?: string;
        /**
         * Moved to title props
         * @deprecated
         * */
        title?: string;
        /**
         * Moved to title props
         * @deprecated
         * */
        description?: string;
        xDescription?: string;
        yDescription?: string;
        showTotal?: boolean;
        /**
         * `125px` is for old charts which were made before BL-5304 to keep height 125px when it was
         * `regular` is for new charts
         */
        height?: '125px' | 'regular';
        hideLegendAfterLines?: number;
      };
      title: ElementLabelProps & {
        description?: string;
      };
      alert?: {
        colorMainValue?: boolean;
        colorChartOutline?: boolean;
        logic?: '+' | '-' | undefined;
      };
      data?: ChartPropsModelData;
      format?: {
        delta?: 'percent' | 'value' | 'none';
        type?: 'Number' | 'Percent' | 'Currency';
        precision: NumberPrecision;
        currency: string;
        dateXFormat?: DateXFormatOptions;
        dateSeriesFormat?: Exclude<
          DATE_FORMAT_OPTIONS_VALUES,
          'short-month-only' | 'long-month-only'
        >;
        curvedLines?: boolean;
      };
      link?: ElementLink;
      font: {
        style: PropertiesConfigInputFontStyleOptions;
        fontSize: ValueWithUnitOfMeasure;
        fontFamily: string;
        lineHeight: ValueWithUnitOfMeasure;
      };
    };
    style: {
      general: {
        title: PropertiesConfigInputColorModel;
        description: PropertiesConfigInputColorModel;
        value?: PropertiesConfigInputColorModel;
        align?: ElementHorizontalAlignment;
        axis?: PropertiesConfigInputColorModel;
        offset: PropertiesConfigInputSpacingAndAlignmentModel;
      };
      titleBlock: ElementTitleStyleProps;
      background: {
        fill: PropertiesConfigInputColorModel;
        outline: PropertiesConfigInputColorModel;
        width: ValueWithUnitOfMeasure;
        radius: BorderRadiusValue;
        visualEffect: PropertiesConfigInputVisualEffectModel;
      };
      alert?: {
        neutral: PropertiesConfigInputColorModel;
        danger: PropertiesConfigInputColorModel;
        success: PropertiesConfigInputColorModel;
      };
      chart?: {
        palette: PropertiesConfigInputColorPaletteModel;
        gridLines?: PropertiesConfigInputColorModel;
        gridDensity?: 'regular' | 'dense';
        line?: PropertiesConfigInputColorModel;
        iconSize?: ValueWithUnitOfMeasure;
      };
    };
  };

//#endregion

export type ChartValuePropsModel = CanvasElementWithDirtyFieldProps &
  CanvasElementPropertiesWithData & {
    /**
     * @deprecated
     * - 2 BL-7402 Add description color setting to Number Chart
     * - 1 Initial
     */
    version: number;
    content: ChartPropsModel['content'] & {
      options?: {
        valueMinHeightEnabled?: boolean;
      };
    };
    style: Omit<ChartPropsModel['style'], 'general'> & {
      general: ChartPropsModel['style']['general'];
      valueFont?: {
        fontFamily: string;
        fontWeight?: FontWeightOption;
        style: PropertiesConfigInputFontStyleOptions;
        fontSizeEnabled?: boolean;
        fontSize?: ValueWithUnitOfMeasure;
      };
    };
  };
